import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, BACKEND_URL } from "../../App";
import { toast } from "sonner";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Card, CardContent } from "../../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Switch } from "../../components/ui/switch";
import PageHeader from "../../components/PageHeader";
import StatusBadge from "../../components/StatusBadge";
import { whatsappUrl } from "../../lib/whatsapp";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../../components/ui/alert-dialog";
import { 
  Plus, Search, FileText, Copy, Trash2, Edit, DollarSign, Download,
  MessageCircle, User, X, FileSignature, Send, Eye, CheckCircle2
} from "lucide-react";

const TIPOS_EVENTO = ["Boda", "XV Anos", "Bautizo", "Primera Comunion", "Cumpleanos", "Graduacion", "Baby Shower", "Corporativo", "Otro"];

const Cotizaciones = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [cotizaciones, setCotizaciones] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [catalogo, setCatalogo] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCot, setEditingCot] = useState(null);
  const [deleteCotId, setDeleteCotId] = useState(null);

  const emptyForm = {
    cliente_id: "",
    // Evento
    fecha_evento: "", horario: "", lugar: "", tipo_evento: "",
    // Invitados
    adultos: 0, jovenes: 0, ninos: 0, bebes: 0,
    // Servicios descriptivos
    paquete: "", menu: "", botanas: "", bebidas: "",
    decoracion: "", area_privada: "", extras: "",
    personal_extra: "", horas_extra: "", observaciones: "",
    // Items con precio
    servicios: [],
    // Financiero
    subtotal: 0, descuento: 0, iva: false, iva_monto: 0,
    total: 0, anticipo: 0, saldo: 0,
    condiciones: "Anticipo para confirmar fecha\nSaldo 5 dias antes del evento",
    vigencia: "15 dias"
  };
  const [formData, setFormData] = useState(emptyForm);
  const [nuevoServicio, setNuevoServicio] = useState({ nombre: "", descripcion: "", precio: "", cantidad: 1 });

  const fetchData = useCallback(async () => {
    try {
      const [cotRes, cliRes, catRes] = await Promise.all([
        axios.get(`${API}/cotizaciones`),
        axios.get(`${API}/clientes`),
        axios.get(`${API}/catalogo`)
      ]);
      setCotizaciones(cotRes.data);
      setClientes(cliRes.data);
      setCatalogo(catRes.data.filter(c => c.activo));
    } catch {
      toast.error("Error al cargar datos");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Auto-open new dialog if ?nuevo=1&cliente_id=xxx
  useEffect(() => {
    const clienteId = searchParams.get("cliente_id");
    if (searchParams.get("nuevo") && clienteId && !loading) {
      const cl = clientes.find(c => c.cliente_id === clienteId);
      if (cl) {
        setFormData(prev => ({
          ...prev,
          cliente_id: clienteId,
          tipo_evento: cl.tipo_evento || "",
          fecha_evento: cl.fecha_evento || "",
        }));
        setDialogOpen(true);
      }
    }
  }, [searchParams, clientes, loading]);

  // Recalculate totals
  const recalc = (servicios, descuento, iva, anticipo) => {
    const subtotal = servicios.reduce((sum, s) => sum + (parseFloat(s.precio) || 0) * (parseInt(s.cantidad) || 1), 0);
    const desc = parseFloat(descuento) || 0;
    const base = subtotal - desc;
    const ivaMonto = iva ? Math.round(base * 0.16 * 100) / 100 : 0;
    const total = Math.round((base + ivaMonto) * 100) / 100;
    const ant = parseFloat(anticipo) || 0;
    return { subtotal, iva_monto: ivaMonto, total, saldo: Math.round((total - ant) * 100) / 100 };
  };

  const updateFinanciero = (updates = {}) => {
    setFormData(prev => {
      const next = { ...prev, ...updates };
      const calc = recalc(next.servicios, next.descuento, next.iva, next.anticipo);
      return { ...next, ...calc };
    });
  };

  const addServicio = () => {
    if (!nuevoServicio.nombre || !nuevoServicio.precio) { toast.error("Nombre y precio requeridos"); return; }
    const srv = { ...nuevoServicio, precio: parseFloat(nuevoServicio.precio) || 0, cantidad: parseInt(nuevoServicio.cantidad) || 1 };
    updateFinanciero({ servicios: [...formData.servicios, srv] });
    setNuevoServicio({ nombre: "", descripcion: "", precio: "", cantidad: 1 });
  };

  const addFromCatalogo = (item) => {
    const srv = { nombre: item.nombre, descripcion: item.descripcion || "", precio: item.precio, cantidad: 1 };
    updateFinanciero({ servicios: [...formData.servicios, srv] });
    toast.success(`${item.nombre} agregado`);
  };

  const removeServicio = (idx) => {
    updateFinanciero({ servicios: formData.servicios.filter((_, i) => i !== idx) });
  };

  const updateServicio = (idx, field, value) => {
    const updated = formData.servicios.map((s, i) => i === idx ? { ...s, [field]: value } : s);
    updateFinanciero({ servicios: updated });
  };

  const handleSave = async () => {
    if (!formData.cliente_id) { toast.error("Selecciona un cliente"); return; }
    try {
      if (editingCot) {
        await axios.put(`${API}/cotizaciones/${editingCot.cotizacion_id}`, formData);
        toast.success("Cotizacion actualizada");
      } else {
        await axios.post(`${API}/cotizaciones`, formData);
        toast.success("Cotizacion creada");
      }
      setDialogOpen(false);
      setEditingCot(null);
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar");
    }
  };

  const handleEliminar = async () => {
    if (!deleteCotId) return;
    try {
      await axios.delete(`${API}/cotizaciones/${deleteCotId}`);
      toast.success("Cotizacion eliminada");
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al eliminar");
    } finally { setDeleteCotId(null); }
  };

  const handleDuplicar = async (cotId) => {
    try {
      await axios.post(`${API}/cotizaciones/${cotId}/duplicar`);
      toast.success("Cotizacion duplicada");
      await fetchData();
    } catch { toast.error("Error al duplicar"); }
  };

  const handleConvertirContrato = async (cot) => {
    try {
      const res = await axios.post(`${API}/cotizaciones/${cot.cotizacion_id}/convertir-contrato`);
      toast.success("Contrato creado exitosamente");
      navigate(`/panel/contratos`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al convertir");
    }
  };

  const handleUpdateEstatus = async (cotId, estatus) => {
    try {
      // Iter105 · Map de estatus visible → backend (retro-compat con datos existentes)
      const backendMap = { nuevo: "borrador", cotizado: "enviada", contratado: "aceptada", cancelado: "rechazada", finalizado: "finalizado" };
      const estatusBackend = backendMap[estatus] || estatus;
      await axios.put(`${API}/cotizaciones/${cotId}`, { estatus: estatusBackend });
      toast.success("Estado actualizado");
      await fetchData();
    } catch { toast.error("Error al actualizar"); }
  };

  // Iter124h · Aprobar cotización (idempotente en backend). Congela snapshot.
  const handleAprobar = async (cot) => {
    if (cot.snapshot_at) { toast.info("Esta cotización ya está aprobada (snapshot congelado)."); return; }
    if (!window.confirm("¿Aprobar cotización? Se congelará como snapshot inmutable. Después de aprobar no podrás modificar precios ni conceptos.")) return;
    try {
      await axios.put(`${API}/cotizaciones/${cot.cotizacion_id}`, { estatus: "aceptada" });
      toast.success("Cotización aprobada · snapshot congelado");
      await fetchData();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error al aprobar"); }
  };

  // Iter124h · Ver vista previa profesional (nueva pestaña con HTML)
  const verVistaPrevia = (cot) => {
    window.open(`${BACKEND_URL}/api/cotizaciones/${cot.cotizacion_id}/pdf/vista`, "_blank", "noopener,noreferrer");
  };

  // Map de estatus backend → visible
  const normEstatus = (raw) => {
    const m = { borrador: "nuevo", enviada: "cotizado", publica: "cotizado", pendiente_revision: "cotizado", aceptada: "contratado", aprobada: "contratado", rechazada: "cancelado", cancelada: "cancelado", finalizado: "finalizado" };
    return m[raw] || raw || "nuevo";
  };

  const enviarWhatsApp = (cot) => {
    const cl = clientes.find(c => c.cliente_id === cot.cliente_id);
    if (!cl?.whatsapp) { toast.error("Cliente sin WhatsApp"); return; }
    let msg = `Hola ${cl.nombre}, aqui tienes tu cotizacion:\n\n`;
    if (cot.tipo_evento) msg += `Evento: ${cot.tipo_evento}\n`;
    if (cot.fecha_evento) msg += `Fecha: ${cot.fecha_evento}\n`;
    msg += `Total: $${(cot.total || 0).toLocaleString()} MXN\n`;
    if (cot.anticipo) msg += `Anticipo: $${cot.anticipo.toLocaleString()} MXN\n`;
    msg += `\nDescarga tu cotizacion:\n${BACKEND_URL}/api/cotizaciones/${cot.cotizacion_id}/pdf/download`;
    const url = whatsappUrl(cl.whatsapp, msg);
    if (!url) { toast.error("El WhatsApp del cliente no es un número válido"); return; }
    window.open(url, "_blank", "noopener,noreferrer");
  };

  const openEditDialog = (cot) => {
    setEditingCot(cot);
    setFormData({
      cliente_id: cot.cliente_id,
      fecha_evento: cot.fecha_evento || "", horario: cot.horario || "", lugar: cot.lugar || "",
      tipo_evento: cot.tipo_evento || "",
      adultos: cot.adultos || 0, jovenes: cot.jovenes || 0, ninos: cot.ninos || 0, bebes: cot.bebes || 0,
      paquete: cot.paquete || "", menu: cot.menu || "", botanas: cot.botanas || "",
      bebidas: cot.bebidas || "", decoracion: cot.decoracion || "", area_privada: cot.area_privada || "",
      extras: cot.extras || "", personal_extra: cot.personal_extra || "", horas_extra: cot.horas_extra || "",
      observaciones: cot.observaciones || "",
      servicios: (cot.servicios || []).map(s => ({ ...s, cantidad: s.cantidad || 1 })),
      subtotal: cot.subtotal || 0, descuento: cot.descuento || 0,
      iva: cot.iva || false, iva_monto: cot.iva_monto || 0,
      total: cot.total || 0, anticipo: cot.anticipo || 0, saldo: cot.saldo || 0,
      condiciones: cot.condiciones || "", vigencia: cot.vigencia || "15 dias"
    });
    setDialogOpen(true);
  };

  const filteredCotizaciones = cotizaciones.filter(cot => {
    if (!searchTerm) return true;
    const cl = clientes.find(c => c.cliente_id === cot.cliente_id);
    const t = searchTerm.toLowerCase();
    return cl?.nombre?.toLowerCase().includes(t) || cot.tipo_evento?.toLowerCase().includes(t) || cot.cotizacion_id?.includes(t);
  });

  const stats = {
    total: cotizaciones.length,
    aceptadas: cotizaciones.filter(c => normEstatus(c.estatus) === "contratado").length,
    valor: cotizaciones.reduce((s, c) => s + (c.total || 0), 0)
  };

  // Iter105 · Estatus alineados al flujo real del negocio
  const ESTATUS_COTIZACION = [
    { key: "nuevo",       label: "Nuevo",       clase: "bg-stone-100 text-stone-700" },
    { key: "cotizado",    label: "Cotizado",    clase: "bg-blue-100 text-blue-700" },
    { key: "contratado",  label: "Contratado",  clase: "bg-emerald-100 text-emerald-700" },
    { key: "cancelado",   label: "Cancelado",   clase: "bg-red-100 text-red-700" },
    { key: "finalizado",  label: "Finalizado",  clase: "bg-purple-100 text-purple-700" },
  ];
  const estatusInfo = (key) => ESTATUS_COTIZACION.find(e => e.key === key) || { label: key || "—", clase: "bg-stone-100 text-stone-600" };

  return (
    <div className="space-y-6" data-testid="cotizaciones-page">
      <PageHeader
        title="Cotizaciones"
        description="Crea cotizaciones formales para tus clientes. Cuando se apruebe y se reciba anticipo, podras generar el contrato."
        primaryAction={{
          label: "Nueva cotizacion",
          icon: Plus,
          testId: "btn-nueva-cotizacion",
          onClick: () => { setEditingCot(null); setFormData(emptyForm); setDialogOpen(true); },
        }}
      />

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-stone-200"><CardContent className="p-3 sm:p-4 text-center">
          <p className="text-xs text-stone-400 uppercase">Total</p>
          <p className="font-display text-xl sm:text-2xl text-stone-900">{stats.total}</p>
        </CardContent></Card>
        <Card className="border-stone-200"><CardContent className="p-3 sm:p-4 text-center">
          <p className="text-xs text-stone-400 uppercase">Aceptadas</p>
          <p className="font-display text-xl sm:text-2xl text-rose-600">{stats.aceptadas}</p>
        </CardContent></Card>
        <Card className="border-stone-200 bg-rose-900"><CardContent className="p-3 sm:p-4 text-center">
          <p className="text-xs text-rose-200 uppercase">Valor</p>
          <p className="font-display text-xl sm:text-2xl text-white">${stats.valor.toLocaleString()}</p>
        </CardContent></Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
        <Input placeholder="Buscar..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)} className="pl-10 border-stone-300 h-9" />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-rose-900 border-t-transparent rounded-full animate-spin" /></div>
      ) : filteredCotizaciones.length === 0 ? (
        <div className="text-center py-16">
          <FileText className="w-12 h-12 text-stone-300 mx-auto mb-3" />
          <h3 className="font-display text-lg text-stone-900">No hay cotizaciones</h3>
          <p className="text-stone-500 text-sm">Crea tu primera cotizacion</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredCotizaciones.map(cot => {
            const cl = clientes.find(c => c.cliente_id === cot.cliente_id);
            const totalInv = (cot.adultos || 0) + (cot.jovenes || 0) + (cot.ninos || 0) + (cot.bebes || 0);
            return (
              <Card key={cot.cotizacion_id} className="border-stone-200" data-testid={`cot-card-${cot.cotizacion_id}`}>
                <CardContent className="p-4">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <User className="w-4 h-4 text-stone-400 shrink-0" />
                        <span className="font-medium text-stone-900 truncate">{cl?.nombre || "Sin cliente"}</span>
                        <select
                          value={normEstatus(cot.estatus)}
                          onChange={(e) => handleUpdateEstatus(cot.cotizacion_id, e.target.value)}
                          className={`text-[10px] px-2 py-0.5 rounded-full font-medium border-0 cursor-pointer ${estatusInfo(normEstatus(cot.estatus)).clase}`}
                          data-testid={`estatus-cot-${cot.cotizacion_id}`}
                          onClick={(e) => e.stopPropagation()}
                          title="Cambiar estado"
                        >
                          {ESTATUS_COTIZACION.map(o => (
                            <option key={o.key} value={o.key}>{o.label}</option>
                          ))}
                        </select>
                      </div>
                      <p className="text-xs text-stone-500 mt-0.5 truncate">
                        {cot.tipo_evento || cl?.tipo_evento || ""} {cot.fecha_evento ? `| ${cot.fecha_evento}` : ""} {totalInv > 0 ? `| ${totalInv} inv.` : ""}
                      </p>
                    </div>
                    <div className="flex items-center gap-2 sm:gap-4 flex-wrap">
                      <div className="text-right">
                        <p className="font-display text-lg text-stone-900">${(cot.total || 0).toLocaleString()}</p>
                        {cot.anticipo > 0 && <p className="text-[10px] text-rose-600">Ant: ${cot.anticipo.toLocaleString()}</p>}
                      </div>
                      <div className="flex items-center gap-1 flex-wrap">
                        {/* Iter124h · Vista previa profesional (HTML del PDF final) */}
                        <Button variant="outline" size="sm" onClick={() => verVistaPrevia(cot)} className="border-stone-200 h-8 w-8 p-0" title="Vista previa" data-testid={`btn-vista-cot-${cot.cotizacion_id}`}>
                          <Eye className="w-3.5 h-3.5" />
                        </Button>
                        {/* Iter124h · PDF profesional (WeasyPrint) */}
                        <a href={`${BACKEND_URL}/api/cotizaciones/${cot.cotizacion_id}/pdf/final`} target="_blank" rel="noopener noreferrer"
                          className="inline-flex items-center justify-center rounded-md border border-stone-200 bg-white hover:bg-stone-50 h-8 w-8" title="PDF"
                          data-testid={`btn-pdf-cot-${cot.cotizacion_id}`}>
                          <Download className="w-3.5 h-3.5" />
                        </a>
                        <Button variant="outline" size="sm" onClick={() => openEditDialog(cot)} className="border-stone-200 h-8 w-8 p-0" title="Editar" data-testid={`btn-edit-cot-${cot.cotizacion_id}`}>
                          <Edit className="w-3.5 h-3.5" />
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => handleDuplicar(cot.cotizacion_id)} className="border-stone-200 h-8 w-8 p-0" title="Duplicar">
                          <Copy className="w-3.5 h-3.5" />
                        </Button>
                        {cl?.whatsapp && (
                          <Button variant="outline" size="sm" onClick={() => enviarWhatsApp(cot)} className="border-green-200 text-green-700 h-8 w-8 p-0" title="WhatsApp">
                            <MessageCircle className="w-3.5 h-3.5" />
                          </Button>
                        )}
                        {(normEstatus(cot.estatus) === "nuevo") && (
                          <Button size="sm" onClick={() => handleUpdateEstatus(cot.cotizacion_id, "cotizado")} className="bg-blue-600 hover:bg-blue-700 text-white text-xs h-8 px-2" data-testid={`btn-enviar-${cot.cotizacion_id}`}>
                            <Send className="w-3 h-3 mr-1" /> Enviar
                          </Button>
                        )}
                        {/* Iter124h · Aprobar (idempotente, congela snapshot) */}
                        {!cot.snapshot_at && (normEstatus(cot.estatus) === "cotizado" || normEstatus(cot.estatus) === "nuevo") && (
                          <Button size="sm" onClick={() => handleAprobar(cot)} className="bg-emerald-700 hover:bg-emerald-800 text-white text-xs h-8 px-2" data-testid={`btn-aprobar-${cot.cotizacion_id}`}>
                            <CheckCircle2 className="w-3 h-3 mr-1" /> Aprobar
                          </Button>
                        )}
                        {(normEstatus(cot.estatus) === "cotizado" || normEstatus(cot.estatus) === "nuevo" || normEstatus(cot.estatus) === "contratado") && (
                          <Button size="sm" onClick={() => handleConvertirContrato(cot)} className="bg-rose-700 hover:bg-rose-800 text-white text-xs h-8 px-2" data-testid={`btn-convertir-${cot.cotizacion_id}`}>
                            <FileSignature className="w-3 h-3 mr-1" /> Contrato
                          </Button>
                        )}
                        <Button variant="outline" size="sm" onClick={() => setDeleteCotId(cot.cotizacion_id)} className="border-red-200 text-red-600 h-8 w-8 p-0">
                          <Trash2 className="w-3.5 h-3.5" />
                        </Button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ==================== FORM DIALOG ==================== */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[92vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{editingCot ? "Editar Cotizacion" : "Nueva Cotizacion"}</DialogTitle>
            <DialogDescription className="text-sm text-stone-500">Completa los datos del evento y servicios</DialogDescription>
          </DialogHeader>
          <div className="space-y-5 py-2">

            {/* Cliente */}
            <div className="space-y-1.5">
              <Label className="text-xs uppercase text-stone-400 tracking-wider">Cliente</Label>
              <Select value={formData.cliente_id} onValueChange={v => setFormData(p => ({ ...p, cliente_id: v }))}>
                <SelectTrigger className="border-stone-300" data-testid="select-cliente-cot"><SelectValue placeholder="Selecciona cliente" /></SelectTrigger>
                <SelectContent>
                  {clientes.map(cl => <SelectItem key={cl.cliente_id} value={cl.cliente_id}>{cl.nombre} - {cl.tipo_evento || "Sin tipo"}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Evento */}
            <div className="border border-stone-200 rounded-sm p-4 space-y-3">
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Datos del Evento</p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="space-y-1 col-span-2 sm:col-span-1">
                  <Label className="text-xs">Tipo</Label>
                  <Select value={formData.tipo_evento} onValueChange={v => setFormData(p => ({ ...p, tipo_evento: v }))}>
                    <SelectTrigger className="border-stone-300 h-9 text-sm"><SelectValue placeholder="Tipo" /></SelectTrigger>
                    <SelectContent>{TIPOS_EVENTO.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1"><Label className="text-xs">Fecha</Label><Input type="date" value={formData.fecha_evento} onChange={e => setFormData(p => ({ ...p, fecha_evento: e.target.value }))} className="border-stone-300 h-9 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Horario</Label><Input value={formData.horario} onChange={e => setFormData(p => ({ ...p, horario: e.target.value }))} placeholder="Ej: 14:00 - 20:00" className="border-stone-300 h-9 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Lugar</Label><Input value={formData.lugar} onChange={e => setFormData(p => ({ ...p, lugar: e.target.value }))} placeholder="Nombre del lugar" className="border-stone-300 h-9 text-sm" /></div>
              </div>
              <div className="grid grid-cols-4 gap-3">
                <div className="space-y-1"><Label className="text-xs">Adultos</Label><Input type="number" value={formData.adultos || ""} onChange={e => setFormData(p => ({ ...p, adultos: parseInt(e.target.value) || 0 }))} className="border-stone-300 h-9 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Jovenes</Label><Input type="number" value={formData.jovenes || ""} onChange={e => setFormData(p => ({ ...p, jovenes: parseInt(e.target.value) || 0 }))} className="border-stone-300 h-9 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Ninos</Label><Input type="number" value={formData.ninos || ""} onChange={e => setFormData(p => ({ ...p, ninos: parseInt(e.target.value) || 0 }))} className="border-stone-300 h-9 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Bebes</Label><Input type="number" value={formData.bebes || ""} onChange={e => setFormData(p => ({ ...p, bebes: parseInt(e.target.value) || 0 }))} className="border-stone-300 h-9 text-sm" /></div>
              </div>
            </div>

            {/* Servicios descriptivos */}
            <div className="border border-stone-200 rounded-sm p-4 space-y-3">
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Detalle de Servicios</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1"><Label className="text-xs">Paquete</Label><Input value={formData.paquete} onChange={e => setFormData(p => ({ ...p, paquete: e.target.value }))} placeholder="Ej: Paquete Premium 100 personas" className="border-stone-300 h-9 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Menu</Label><Input value={formData.menu} onChange={e => setFormData(p => ({ ...p, menu: e.target.value }))} placeholder="Ej: 3 tiempos + postre" className="border-stone-300 h-9 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Botanas</Label><Input value={formData.botanas} onChange={e => setFormData(p => ({ ...p, botanas: e.target.value }))} placeholder="Ej: Tabla de quesos, guacamole" className="border-stone-300 h-9 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Bebidas</Label><Input value={formData.bebidas} onChange={e => setFormData(p => ({ ...p, bebidas: e.target.value }))} placeholder="Ej: Barra libre 5hrs" className="border-stone-300 h-9 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Decoracion</Label><Input value={formData.decoracion} onChange={e => setFormData(p => ({ ...p, decoracion: e.target.value }))} placeholder="Ej: Tematica tropical" className="border-stone-300 h-9 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Area privada</Label><Input value={formData.area_privada} onChange={e => setFormData(p => ({ ...p, area_privada: e.target.value }))} placeholder="Ej: Salon principal + terraza" className="border-stone-300 h-9 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Extras</Label><Input value={formData.extras} onChange={e => setFormData(p => ({ ...p, extras: e.target.value }))} placeholder="Ej: DJ, foto, video" className="border-stone-300 h-9 text-sm" /></div>
                <div className="space-y-1"><Label className="text-xs">Personal extra</Label><Input value={formData.personal_extra} onChange={e => setFormData(p => ({ ...p, personal_extra: e.target.value }))} placeholder="Ej: 2 meseros adicionales" className="border-stone-300 h-9 text-sm" /></div>
                <div className="space-y-1 col-span-1 sm:col-span-2"><Label className="text-xs">Horas extra</Label><Input value={formData.horas_extra} onChange={e => setFormData(p => ({ ...p, horas_extra: e.target.value }))} placeholder="Ej: 2 hrs extra ($2,000/hr)" className="border-stone-300 h-9 text-sm" /></div>
              </div>
            </div>

            {/* Servicios con precio (tabla) */}
            <div className="border border-stone-200 rounded-sm p-4 space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Servicios con Costo</p>
              </div>

              {/* Add from catalogo */}
              {catalogo.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {catalogo.slice(0, 8).map(item => (
                    <button key={item.item_id} onClick={() => addFromCatalogo(item)} className="text-xs bg-rose-50 text-rose-700 border border-rose-200 rounded px-2 py-1 hover:bg-rose-100 transition-colors truncate max-w-[150px]">
                      + {item.nombre}
                    </button>
                  ))}
                </div>
              )}

              {/* Add manual */}
              <div className="flex gap-2 items-end">
                <div className="flex-1 space-y-1"><Label className="text-xs">Nombre</Label><Input value={nuevoServicio.nombre} onChange={e => setNuevoServicio(p => ({ ...p, nombre: e.target.value }))} placeholder="Servicio" className="border-stone-300 h-8 text-sm" /></div>
                <div className="w-16 space-y-1"><Label className="text-xs">Cant.</Label><Input type="number" value={nuevoServicio.cantidad} onChange={e => setNuevoServicio(p => ({ ...p, cantidad: parseInt(e.target.value) || 1 }))} className="border-stone-300 h-8 text-sm" /></div>
                <div className="w-24 space-y-1"><Label className="text-xs">Precio</Label><Input type="number" value={nuevoServicio.precio} onChange={e => setNuevoServicio(p => ({ ...p, precio: e.target.value }))} placeholder="$0" className="border-stone-300 h-8 text-sm" /></div>
                <Button size="sm" onClick={addServicio} className="btn-primary h-8 px-3"><Plus className="w-3 h-3" /></Button>
              </div>

              {/* Table */}
              {formData.servicios.length > 0 && (
                <div className="border border-stone-100 rounded overflow-hidden text-sm">
                  <div className="grid grid-cols-[1fr_55px_75px_75px_30px] bg-stone-50 text-[10px] font-medium text-stone-400 uppercase tracking-wider px-2 py-1.5">
                    <span>Servicio</span><span className="text-center">Cant</span><span className="text-right">P.U.</span><span className="text-right">Total</span><span></span>
                  </div>
                  {formData.servicios.map((s, i) => (
                    <div key={`s-${i}`} className="grid grid-cols-[1fr_55px_75px_75px_30px] items-center border-t border-stone-50 px-2 py-1">
                      <span className="text-sm truncate">{s.nombre}</span>
                      <Input type="number" value={s.cantidad} onChange={e => updateServicio(i, "cantidad", parseInt(e.target.value) || 1)} className="border-0 h-7 text-xs text-center p-0 focus-visible:ring-0" />
                      <Input type="number" value={s.precio} onChange={e => updateServicio(i, "precio", parseFloat(e.target.value) || 0)} className="border-0 h-7 text-xs text-right p-0 focus-visible:ring-0" />
                      <span className="text-xs font-medium text-right">${((parseFloat(s.precio) || 0) * (parseInt(s.cantidad) || 1)).toLocaleString()}</span>
                      <button onClick={() => removeServicio(i)} className="text-stone-400 hover:text-red-500 flex justify-center"><X className="w-3.5 h-3.5" /></button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Financiero */}
            <div className="border border-stone-200 rounded-sm p-4 space-y-3">
              <p className="text-xs font-medium text-stone-500 uppercase tracking-wider">Resumen Financiero</p>
              <div className="space-y-2">
                <div className="flex justify-between text-sm"><span className="text-stone-500">Subtotal</span><span className="font-medium">${formData.subtotal.toLocaleString()} MXN</span></div>
                <div className="flex items-center gap-3">
                  <Label className="text-sm text-stone-500 w-20">Descuento</Label>
                  <Input type="number" value={formData.descuento || ""} onChange={e => updateFinanciero({ descuento: parseFloat(e.target.value) || 0 })} className="border-stone-300 h-8 text-sm flex-1" placeholder="$0" />
                </div>
                <div className="flex items-center gap-3">
                  <Label className="text-sm text-stone-500 w-20">IVA (16%)</Label>
                  <Switch checked={formData.iva} onCheckedChange={v => updateFinanciero({ iva: v })} />
                  {formData.iva && <span className="text-sm text-stone-600">${formData.iva_monto.toLocaleString()} MXN</span>}
                </div>
                <div className="flex justify-between text-base font-display border-t border-stone-200 pt-2"><span className="text-stone-900">TOTAL</span><span className="text-rose-800">${formData.total.toLocaleString()} MXN</span></div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Anticipo ($)</Label>
                    <Input type="number" value={formData.anticipo || ""} onChange={e => updateFinanciero({ anticipo: parseFloat(e.target.value) || 0 })} className="border-stone-300 h-8 text-sm" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">Saldo pendiente</Label>
                    <div className={`border border-stone-200 rounded-sm px-3 py-1.5 text-sm font-medium ${formData.saldo > 0 ? 'text-amber-600' : 'text-rose-600'}`}>${formData.saldo.toLocaleString()} MXN</div>
                  </div>
                </div>
              </div>
            </div>

            {/* Observaciones y condiciones */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label className="text-xs">Observaciones</Label><Textarea value={formData.observaciones} onChange={e => setFormData(p => ({ ...p, observaciones: e.target.value }))} rows={3} placeholder="Notas adicionales..." className="border-stone-300 text-sm" /></div>
              <div className="space-y-1.5"><Label className="text-xs">Condiciones</Label><Textarea value={formData.condiciones} onChange={e => setFormData(p => ({ ...p, condiciones: e.target.value }))} rows={3} className="border-stone-300 text-sm" /></div>
            </div>
            <div className="space-y-1.5"><Label className="text-xs">Vigencia</Label><Input value={formData.vigencia} onChange={e => setFormData(p => ({ ...p, vigencia: e.target.value }))} className="border-stone-300 h-8 text-sm w-40" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="border-stone-300">Cancelar</Button>
            <Button onClick={handleSave} className="btn-primary" data-testid="btn-guardar-cotizacion">{editingCot ? "Actualizar" : "Crear"} Cotizacion</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={!!deleteCotId} onOpenChange={() => setDeleteCotId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar cotizacion?</AlertDialogTitle>
            <AlertDialogDescription>Si esta vinculada a un contrato, no podra eliminarse.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleEliminar} className="bg-red-600 hover:bg-red-700" data-testid="btn-confirmar-eliminar-cot">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default Cotizaciones;
